import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
    getCategoryListingPaginate,
    getSubCategoryListingPaginate,
    getManufacturesListingPaginate,
} from "../../../api/context/manufacturer";
import { getBrandListing } from "../../../api/context/brand";

const FilterModal = ({ show, onHide, onApplyFilters, searchFormik, userType }) => {
    const [activeFilter, setActiveFilter] = useState("Category");
    const isAdmin = userType === "ADMIN";

    // Initialize searchQueries with all filter types
    const [searchQueries, setSearchQueries] = useState({
        Category: "",
        Subcategory: "",
        Brands: "",
        Vendors: "",
        Stock: "",
    });

    // Use backend field names directly
    const [localSelections, setLocalSelections] = useState({
        category: [],           // Changed from selectedCategoryList
        subCategory: [],        // Changed from selectedSubCategory
        brands: [],             // Changed from selected_brands
        vendors: [],            // Changed from selected_vendors
        stock: [],
        minPrice: 0,
        maxPrice: 99999,
    });

    const [allCategories, setAllCategories] = useState([]);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [brandList, setBrandList] = useState([]);
    const [vendorList, setVendorList] = useState([]);
    const [loadingSubCategories, setLoadingSubCategories] = useState(false);

    const stockList = [
        { id: "in_stock", name: "In Stock" },
        { id: "low_stock", name: "Low Stock" },
        { id: "out_of_stock", name: "Out of Stock" },
    ];

    const filterTypes = [
        { key: "Category", stateKey: "category" },
        { key: "Subcategory", stateKey: "subCategory" },
        ...(isAdmin ? [
            { key: "Brands", stateKey: "brands" },
            { key: "Vendors", stateKey: "vendors" },
        ] : []),
        { key: "Stock", stateKey: "stock" },
        { key: "Price Range", stateKey: "priceRange" },
    ];

    // Helper function to map IDs to full objects
    const mapIdsToObjects = (ids, list) => {
        if (!ids || !Array.isArray(ids)) return [];
        return ids
            .map(id => list.find(item => item && item.id === id))
            .filter(item => item !== undefined);
    };

    useEffect(() => {
        const hasRequiredLists = allCategories.length > 0;

        if (show && hasRequiredLists) {
            // Map formik values (IDs) to local selections (objects) only when modal opens with data
            const categoryIds = searchFormik.values.category || searchFormik.values.selectedCategoryList || [];
            const subCategoryIds = searchFormik.values.subCategory || searchFormik.values.selectedSubCategory || [];
            const brandIds = isAdmin ? (searchFormik.values.brands || searchFormik.values.selected_brands || []) : [];
            const vendorIds = isAdmin ? (searchFormik.values.vendors || searchFormik.values.selected_vendors || []) : [];
            const stockIds = searchFormik.values.stock || [];

            setLocalSelections({
                category: mapIdsToObjects(categoryIds, allCategories),
                subCategory: mapIdsToObjects(subCategoryIds, subCategoryList),
                brands: mapIdsToObjects(brandIds, brandList),
                vendors: mapIdsToObjects(vendorIds, vendorList),
                stock: mapIdsToObjects(stockIds, stockList),
                minPrice: searchFormik.values.minPrice || 0,
                maxPrice: searchFormik.values.maxPrice || 99999,
            });
        }
    }, [show, allCategories, brandList, vendorList, isAdmin]);

    useEffect(() => {
        if (show) fetchAllData();
    }, [show]);

    const fetchAllData = async () => {
        try {
            const categories = await fetchCategories();
            setAllCategories(categories);

            if (isAdmin) {
                const brands = await fetchBrands();
                setBrandList(brands);

                const vendors = await fetchManufacturers();
                setVendorList(vendors);
            } else {
                setBrandList([]);
                setVendorList([]);
            }

            await fetchSubCategoriesBasedOnSelection(localSelections.category);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchSubCategoriesBasedOnSelection = async (selectedCategories) => {
        setLoadingSubCategories(true);
        try {
            let categoryIds = [];

            if (selectedCategories.length === 0) {
                categoryIds = allCategories.map(cat => cat.id);
            } else {
                categoryIds = selectedCategories.map(cat => cat.id);
            }

            if (categoryIds.length === 0) {
                setSubCategoryList([]);
                setLoadingSubCategories(false);
                return;
            }

            getSubCategoryListingPaginate({
                page: 1,
                limit: 500,
                search: '',
                categoryIds: categoryIds,
            })((response) => {
                setLoadingSubCategories(false);
                if (response?.statusCode === 200) {
                    const formatted = response.results.map((item) => ({
                        id: item.id || item.value,
                        name: item.categoryName ? `${item.categoryName} - ${item.title || item.name}` : (item.title || item.name)
                    }));

                    setSubCategoryList(formatted);
                } else {
                    setSubCategoryList([]);
                }
            });
        } catch (err) {
            setLoadingSubCategories(false);
            console.error("Error fetching subcategories", err);
        }
    };

    const fetchCategories = () =>
        new Promise((resolve) => {
            getCategoryListingPaginate({ page: 1, limit: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.results.map(item => ({ id: item.value, name: item.label })));
                } else resolve([]);
            });
        });

    const fetchBrands = () =>
        new Promise((resolve) => {
            getBrandListing({ page: 1, pageSize: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.data.map(item => ({ id: item.id, name: item.name })));
                } else resolve([]);
            });
        });

    const fetchManufacturers = () =>
        new Promise((resolve) => {
            getManufacturesListingPaginate({ page: 1, limit: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.results.map(item => ({ id: item.value, name: item.label })));
                } else resolve([]);
            });
        });

    useEffect(() => {
        const hasActiveFilter = filterTypes.some((filterType) => filterType.key === activeFilter);

        if (!hasActiveFilter) {
            setActiveFilter("Category");
        }
    }, [activeFilter, isAdmin]);

    useEffect(() => {
        if (show && allCategories.length > 0) {
            fetchSubCategoriesBasedOnSelection(localSelections.category);
        }
    }, [localSelections.category, show]);

    const toggleSelection = (key, item) => {
        if (!item || !item.id) return;

        const selected = localSelections[key] || [];
        const exists = selected.some((x) => x && x.id === item.id);

        setLocalSelections(prev => ({
            ...prev,
            [key]: exists
                ? selected.filter((x) => x && x.id !== item.id)
                : [...selected.filter(x => x), item]
        }));
    };

    const handleRowClick = (key, item, e) => {
        if (e.target.type === 'checkbox') {
            return;
        }
        toggleSelection(key, item);
    };

    const filterList = (list, filterKey) => {
        const searchQuery = searchQueries[filterKey] || "";
        if (!searchQuery.trim()) {
            return list;
        }
        
        return list.filter(item => {
            if (!item || !item.name) return false;
            return item.name.toLowerCase().includes(searchQuery.toLowerCase());
        });
    };

    const renderCheckboxList = (list, key) => {
        const filtered = filterList(list, activeFilter);
        const selected = localSelections[key] || [];

        if (filtered.length === 0) {
            return (
                <div className="filter-no-items">
                    No items found{searchQueries[activeFilter] ? ` for "${searchQueries[activeFilter]}"` : ""}
                </div>
            );
        }

        return filtered.map((item) => {
            if (!item || !item.id) return null;
            const isChecked = selected.some((x) => x && x.id === item.id);

            return (
                <div
                    key={item.id}
                    onClick={(e) => handleRowClick(key, item, e)}
                    className={`filter-row ${isChecked ? "checked" : ""}`}
                >
                    <input
                        type="checkbox"
                        className="row-check"
                        checked={isChecked}
                        onChange={() => toggleSelection(key, item)}
                    />
                    <div className="row-label">{item.name || "Unnamed Item"}</div>
                </div>
            );
        });
    };

    const getListByKey = (key) => {
        switch (key) {
            case "Category": return allCategories || [];
            case "Subcategory": return subCategoryList || [];
            case "Brands": return brandList || [];
            case "Vendors": return vendorList || [];
            case "Stock": return stockList || [];
            case "Price Range": return []; // Handled separately
            default: return [];
        }
    };

    const getStateKeyForActiveFilter = () => {
        const filter = filterTypes.find(f => f.key === activeFilter);
        return filter ? filter.stateKey : "";
    };

    const handleClearAll = () => {
        setLocalSelections({
            category: [],
            subCategory: [],
            brands: [],
            vendors: [],
            stock: [],
            minPrice: 0,
            maxPrice: 99999,
        });
        // Also clear search queries
        setSearchQueries({
            Category: "",
            Subcategory: "",
            Brands: "",
            Vendors: "",
            Stock: "",
        });
        // Clear formik values as well
        searchFormik.setFieldValue('category', []);
        searchFormik.setFieldValue('subCategory', []);
        searchFormik.setFieldValue('brands', []);
        searchFormik.setFieldValue('vendors', []);
        searchFormik.setFieldValue('stock', []);
        searchFormik.setFieldValue('minPrice', 0);
        searchFormik.setFieldValue('maxPrice', 99999);
    };

    const handleApplyFilters = () => {
        // Convert selections to just IDs for formik
        const formattedValues = {
            category: localSelections.category.map(item => item.id) || [],
            subCategory: localSelections.subCategory.map(item => item.id) || [],
            brands: isAdmin ? (localSelections.brands.map(item => item.id) || []) : [],
            vendors: isAdmin ? (localSelections.vendors.map(item => item.id) || []) : [],
            stock: localSelections.stock.map(item => item.id) || [],
            minPrice: localSelections.minPrice || 0,
            maxPrice: localSelections.maxPrice || 99999,
        };

        // Update formik values
        Object.keys(formattedValues).forEach(key => {
            searchFormik.setFieldValue(key, formattedValues[key]);
        });

        // Trigger the filter apply
        onApplyFilters();
        onHide();
    };

    const handleSearchChange = (value) => {
        setSearchQueries(prev => ({
            ...prev,
            [activeFilter]: value
        }));
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="inventory-filter-modal">
            <style>{`
                /* ===== Desktop styling (default) ===== */
                .inventory-filter-modal .modal-content {
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                }
                .inventory-filter-modal .modal-header {
                    padding: 14px 18px !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                }
                .inventory-filter-modal .modal-title {
                    font-size: 15px !important;
                    font-weight: 700 !important;
                    color: #0f172a !important;
                    letter-spacing: -0.01em;
                }
                .inventory-filter-modal .modal-header .btn-close {
                    width: 12px; height: 12px;
                    padding: 8px;
                    background-size: 10px;
                }
                .inventory-filter-modal .modal-body { padding: 0 !important; }
                .inventory-filter-modal .modal-footer {
                    padding: 10px 16px !important;
                    border-top: 1px solid #e2e8f0 !important;
                    gap: 8px;
                }
                .inventory-filter-modal .modal-footer .btn { font-size: 12px !important; }
                .inventory-filter-modal .modal-footer .request_btn {
                    background: #008695 !important;
                    border: 1px solid #008695 !important;
                    color: #fff !important;
                    font-weight: 600 !important;
                    height: 34px;
                    padding: 6px 18px !important;
                    border-radius: 4px !important;
                    font-size: 12px !important;
                }
                .inventory-filter-modal .modal-footer .request_btn:hover {
                    background: #006672 !important;
                    border-color: #006672 !important;
                }
                .inventory-filter-modal .btn-link {
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    color: #64748b !important;
                    text-decoration: none !important;
                }
                .inventory-filter-modal .btn-link:hover { color: #0f172a !important; }

                /* Body layout — fixed height, internal scroll only */
                .inventory-filter-modal .filter-body-wrap {
                    display: flex;
                    width: 100%;
                    height: 440px;
                    max-height: 60vh;
                    min-height: 0;
                }

                /* Left panel — filter categories */
                .inventory-filter-modal .filter-left-panel {
                    width: 200px;
                    flex-shrink: 0;
                    background: #f8fafc;
                    border-right: 1px solid #e2e8f0;
                    overflow-y: auto;
                    min-height: 0;
                }
                .inventory-filter-modal .filter-cat {
                    padding: 10px 14px;
                    font-size: 12px;
                    color: #475569;
                    cursor: pointer;
                    border-bottom: 1px solid #eef2f7;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 6px;
                }
                .inventory-filter-modal .filter-cat:hover { background: #eef2f7; color: #0f172a; }
                .inventory-filter-modal .filter-cat.active {
                    background: #fff;
                    color: #008695;
                    font-weight: 600;
                    box-shadow: inset 3px 0 0 #008695;
                }
                .inventory-filter-modal .filter-cat-count {
                    font-size: 10px;
                    background: #008695;
                    color: #fff;
                    padding: 1px 6px;
                    border-radius: 999px;
                    font-weight: 700;
                }

                /* Right panel */
                .inventory-filter-modal .filter-right-panel {
                    flex: 1;
                    padding: 16px 18px;
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }
                .inventory-filter-modal .filter-right-title {
                    font-size: 13px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 10px;
                    letter-spacing: -0.01em;
                }
                .inventory-filter-modal .filter-search-input {
                    height: 34px !important;
                    font-size: 12px !important;
                    padding: 6px 10px !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 4px !important;
                    margin-bottom: 10px !important;
                }
                .inventory-filter-modal .filter-search-input:focus {
                    border-color: #008695 !important;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                }
                .inventory-filter-modal .filter-scroll-area {
                    flex: 1 1 auto;
                    overflow-y: auto;
                    min-height: 0;
                }
                .inventory-filter-modal .filter-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px !important;
                    border-radius: 6px;
                    margin-bottom: 2px;
                    cursor: pointer;
                    font-size: 12px;
                    color: #0f172a;
                }
                .inventory-filter-modal .filter-row:hover { background: #f8fafc; }
                .inventory-filter-modal .filter-row.checked { background: #f0fdfa; }
                .inventory-filter-modal .filter-row .row-check {
                    width: 14px; height: 14px;
                    margin: 0;
                    accent-color: #008695;
                    flex-shrink: 0;
                }
                .inventory-filter-modal .filter-row .row-label {
                    flex: 1;
                    min-width: 0;
                    font-size: 12px;
                    line-height: 1.35;
                }
                .inventory-filter-modal .filter-no-items {
                    text-align: center;
                    padding: 24px 12px;
                    font-size: 12px;
                    color: #94a3b8;
                }

                /* Price range panel */
                .inventory-filter-modal .price-range-section .form-label {
                    font-size: 11px !important;
                    font-weight: 600 !important;
                    color: #475569 !important;
                    margin-bottom: 4px !important;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }
                .inventory-filter-modal .price-range-section .form-control {
                    height: 34px !important;
                    font-size: 12px !important;
                    padding: 6px 10px !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 4px !important;
                }
                .inventory-filter-modal .price-range-display {
                    font-size: 12px;
                    color: #0f172a;
                    background: #f8fafc !important;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 10px 12px !important;
                }
                .inventory-filter-modal .price-range-display strong { color: #008695; font-weight: 700; }

                /* ===== Mobile bottom sheet — full width, two-panel body, half/half footer ===== */
                @media (max-width: 767.98px) {
                    /* Kill outer modal padding so the sheet is edge-to-edge */
                    .modal:has(.inventory-filter-modal) { padding: 0 !important; }

                    .inventory-filter-modal.modal-dialog {
                        margin: 0 !important;
                        width: 100vw !important;
                        max-width: 100vw !important;
                        min-width: 100vw !important;
                        position: fixed !important;
                        bottom: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        top: auto !important;
                        transform: none !important;
                        display: flex !important;
                        flex-direction: column;
                        justify-content: flex-end;
                        align-items: stretch !important;
                        min-height: unset !important;
                    }
                    .inventory-filter-modal .modal-content {
                        width: 100% !important;
                        border-radius: 16px 16px 0 0 !important;
                        border: none !important;
                        border-top: 1px solid #e2e8f0 !important;
                        max-height: 88vh;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                    }
                    /* Grab handle */
                    .inventory-filter-modal .modal-content::before {
                        content: "";
                        display: block;
                        width: 40px;
                        height: 4px;
                        background: #cbd5e1;
                        border-radius: 999px;
                        margin: 8px auto 0;
                        flex-shrink: 0;
                    }
                    .inventory-filter-modal .modal-header { padding: 6px 16px 10px !important; }
                    .inventory-filter-modal .modal-body {
                        padding: 0 !important;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                        flex: 1 1 auto;
                        min-height: 0;
                        width: 100%;
                    }

                    /* Two-panel body — stays side by side, fills full sheet width */
                    .inventory-filter-modal .filter-body-wrap {
                        display: flex !important;
                        flex-direction: row !important;
                        width: 100% !important;
                        height: 55vh !important;
                        max-height: 55vh !important;
                        min-height: 0;
                        flex: 1 1 auto;
                    }
                    .inventory-filter-modal .filter-left-panel {
                        width: 40% !important;
                        max-width: none !important;
                        flex-shrink: 0;
                    }
                    .inventory-filter-modal .filter-cat {
                        padding: 10px 12px;
                        font-size: 11px;
                        gap: 4px;
                    }
                    .inventory-filter-modal .filter-cat-count {
                        font-size: 9px;
                        padding: 1px 5px;
                    }
                    .inventory-filter-modal .filter-right-panel {
                        padding: 12px 14px 14px;
                        min-width: 0;
                        flex: 1 1 auto;
                        width: 60%;
                    }
                    .inventory-filter-modal .filter-right-title { font-size: 12px; margin-bottom: 8px; }
                    .inventory-filter-modal .filter-search-input {
                        height: 32px !important;
                        font-size: 11px !important;
                    }
                    .inventory-filter-modal .filter-row {
                        padding: 8px 8px !important;
                        gap: 8px;
                        font-size: 11px;
                    }
                    .inventory-filter-modal .filter-row .row-label { font-size: 11px; }

                    /* Footer — two half-half buttons pinned at the bottom */
                    .inventory-filter-modal .modal-footer {
                        display: flex !important;
                        flex-direction: row !important;
                        flex-wrap: nowrap !important;
                        justify-content: stretch !important;
                        gap: 10px !important;
                        padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
                        border-top: 1px solid #e2e8f0 !important;
                        background: #fff;
                        flex-shrink: 0;
                        width: 100%;
                    }
                    .inventory-filter-modal .modal-footer > * {
                        margin: 0 !important;
                        flex: 1 1 50% !important;
                        width: 50% !important;
                        max-width: 50% !important;
                    }
                    .inventory-filter-modal .modal-footer .btn-link {
                        height: 42px;
                        border: 1px solid #cbd5e1 !important;
                        border-radius: 6px !important;
                        background: #fff !important;
                        color: #0f172a !important;
                        font-weight: 600 !important;
                        font-size: 13px !important;
                        display: flex !important;
                        align-items: center;
                        justify-content: center;
                    }
                    .inventory-filter-modal .modal-footer .request_btn {
                        height: 42px !important;
                        border-radius: 6px !important;
                        font-size: 13px !important;
                        padding: 0 !important;
                        display: flex !important;
                        align-items: center;
                        justify-content: center;
                    }
                }
            `}</style>

            <Modal.Header closeButton>
                <Modal.Title>Filters</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="filter-body-wrap">
                    <div className="filter-left-panel">
                        {filterTypes.map((ft) => {
                            const count = localSelections[ft.stateKey]?.length || 0;
                            const isActive = activeFilter === ft.key;
                            return (
                                <div
                                    key={ft.key}
                                    className={`filter-cat ${isActive ? "active" : ""}`}
                                    onClick={() => setActiveFilter(ft.key)}
                                >
                                    <span>{ft.key}</span>
                                    {count > 0 && <span className="filter-cat-count">{count}</span>}
                                </div>
                            );
                        })}
                    </div>

                    <div className="filter-right-panel">
                        <h6 className="filter-right-title">{activeFilter}</h6>

                        {activeFilter === "Price Range" ? (
                            <div className="price-range-section">
                                <div className="mb-3">
                                    <label className="form-label">Minimum Price</label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Min Price"
                                        value={localSelections.minPrice}
                                        onChange={(e) => setLocalSelections(prev => ({
                                            ...prev,
                                            minPrice: parseInt(e.target.value) || 0
                                        }))}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Maximum Price</label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Max Price"
                                        value={localSelections.maxPrice}
                                        onChange={(e) => setLocalSelections(prev => ({
                                            ...prev,
                                            maxPrice: parseInt(e.target.value) || 99999
                                        }))}
                                    />
                                </div>
                                <div className="price-range-display">
                                    <strong>Range: </strong>₹{localSelections.minPrice} – ₹{localSelections.maxPrice}
                                </div>
                            </div>
                        ) : (
                            <>
                                <Form.Control
                                    type="text"
                                    placeholder={`Search ${activeFilter.toLowerCase()}...`}
                                    className="filter-search-input"
                                    value={searchQueries[activeFilter] || ""}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />

                                <div className="filter-scroll-area">
                                    {renderCheckboxList(
                                        getListByKey(activeFilter),
                                        getStateKeyForActiveFilter()
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="link" onClick={handleClearAll}>Clear all</Button>
                <Button className="request_btn" onClick={handleApplyFilters}>Apply filters</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FilterModal;
